import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as d3 from "d3";
import Spinner from "../spinner";

const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !isNaN(+value)) return value;

  if (typeof value === "string") {
    const trimmed = value.trim();

    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (m) {
      const [, y, mo, d] = m.map(Number);
      return new Date(y, mo - 1, d);
    }
  }

  const d = new Date(value);
  return Number.isFinite(+d) ? d : null;
};

const MIN_TICK_GAP = 60;

const dedupeTicksByGap = (ticks, scale, minGap = MIN_TICK_GAP) => {
  if (!ticks || ticks.length <= 1) return ticks;

  const kept = [];
  let lastX = -Infinity;

  ticks.forEach((tick, index) => {
    const xVal = scale(tick);

    const isLast = index === ticks.length - 1;
    if (isLast) {
      if (xVal - lastX < minGap && kept.length > 1) {
        kept.pop();
      }
      kept.push(tick);
      return;
    }

    if (xVal - lastX >= minGap) {
      kept.push(tick);
      lastX = xVal;
    }
  });

  return kept;
};

export default function RofProgressionChart({
  items = [],            
  height = 400,
  title = "RoF Progression",
  loading = false,       
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [width, setWidth] = useState(1200);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = height;
  const W = Math.max(width, 600);
  const M = { top: 42, right: 72, bottom: 64, left: 60 };

  const LEG_W = 360;
  const legendX = W - M.right - LEG_W;
  const legendY = H;

  const data = useMemo(() => {
    const cleaned = (items || [])
      .map((d) => {
        const date = parseLocalDate(d.date);
        if (!date) return null;

        return {
          ...d,
          date,
          pct: Math.max(0, Math.min(100, d.pct ?? 0)),
          completed: Math.max(1, Math.min(3, Number(d.completed || 1))),
        };
      })
      .filter(Boolean)
      .sort((a, b) => +a.date - +b.date);

    if (!cleaned.length) return cleaned;

    const today = new Date();
    const todayLocal = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const last = cleaned[cleaned.length - 1];

    if (+todayLocal > +last.date) {
      cleaned.push({
        ...last,
        date: todayLocal,
      });
    } else if (cleaned.length === 1) {
      const oneWeek = 7 * 24 * 3600 * 1000;
      cleaned.push({
        ...last,
        date: new Date(+last.date + oneWeek),
      });
    }

    return cleaned;
  }, [items]);



  const x = useMemo(() => {
    const min = d3.min(data, (d) => d.date) ?? new Date();
    const max = d3.max(data, (d) => d.date) ?? new Date();
    const pad = (max - min) * 0.06 || 24 * 3600 * 1000;
    return d3
      .scaleTime()
      .domain([new Date(+min - pad), new Date(+max + pad)])
      .range([M.left, W - M.right]);
  }, [data, W]);

  const y = useMemo(
    () => d3.scaleLinear().domain([0, 100]).nice().range([H - M.bottom, M.top]),
    [H]
  );

  const xTicks = useMemo(() => {
  if (!data.length) return [];

  let ticks;

  if (data.length <= 6) {
    ticks = data.map((d) => d.date);
  } else {
    const raw = [];
    const steps = 5;
    const step = (data.length - 1) / steps;

    for (let i = 0; i <= steps; i++) {
      const idx = Math.round(i * step);
      raw.push(data[idx].date);
    }

    const uniq = Array.from(new Set(raw.map((d) => +d))).map(
      (ms) => new Date(ms)
    );
    ticks = uniq;
  }

  return dedupeTicksByGap(ticks, x);
}, [data, x]);


  const yTicks = useMemo(() => d3.range(0, 101, 20), []);

  const segments = useMemo(() => {
    const out = [];
    for (let i = 0; i < data.length - 1; i++) {
      const a = data[i], b = data[i + 1];
      out.push({
        x1: x(a.date), y1: y(a.pct),
        x2: x(b.date), y2: y(a.pct), 
        completed: a.completed,
      });
    }
    return out;
  }, [data, x, y]);

  const purple = "var(--rof-purple, #8B5CF6)";
  const grid   = "rgba(16,24,40,0.10)";
  const axis   = "rgba(16,24,40,0.65)";
  const text   = "rgba(16,24,40,0.85)";
  const cardShadow =
    "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.06)";

  const styleFor = (completed) => {
    if (completed >= 3) return { dash: "", opacity: 1.0 };
    if (completed === 2) return { dash: "10 10", opacity: 0.75 };
    return { dash: "4 14", opacity: 0.5 };
  };

  const [hover, setHover] = useState(null);
  const fmtDate = d3.timeFormat("%b %d");
  const onPointerMove = (evt) => {
    if (!data.length) return;
    const native = evt.nativeEvent || {};
    const pt =
      (typeof native.offsetX === "number" && native.offsetX) ??
      (typeof native.layerX === "number" && native.layerX) ??
      null;
    if (pt == null) return;
    const idx = data
      .map((d) => Math.abs(x(d.date) - pt))
      .reduce((mi, d, i, arr) => (d < arr[mi] ? i : mi), 0);
    const d = data[idx];
    setHover({
      x: x(d.date), y: y(d.pct),
      date: d.date, pct: Math.round(d.pct), completed: d.completed,
    });
  };

  if (loading) {
    return (
      <div
        ref={containerRef}
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow:
            "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.06)",
          padding: 16,
          marginBottom: 24,
          marginTop: 24,
        }}
      >
        <div
          style={{
            height,                 
            width: "100%",
            display: "grid",
            placeItems: "center",   
            paddingBottom: 60,
          }}
        >
          <Spinner type="big-blue" />
        </div>
      </div>
    );
  }

  const noData = !data.length;

  return (
    <div
      ref={containerRef}
      style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow: cardShadow,
        padding: 16,
        marginBottom: 24,
        marginTop: 24,
      }}
    >
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        onMouseMove={onPointerMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label="Risk of Fall progression over time"
        style={{ cursor: data.length ? "crosshair" : "default" }}
      >
        <text x={M.left} y={24} fontSize="20" fontWeight="600" fill="#1F3A5F">
          {title}
        </text>

        <g transform={`translate(${W - M.right + 50},${M.top - 40})`}>
          <circle r="11" fill="white" stroke={grid} />
          <text textAnchor="middle" dy="0.35em" fontSize="12" fill={axis}>i</text>
        </g>

        {xTicks.map((t, i) => (
          <line key={i} x1={x(t)} x2={x(t)} y1={M.top} y2={H - M.bottom} stroke={grid} strokeDasharray="4 6" />
        ))}

        <line x1={M.left} y1={y(0)} x2={W - M.right} y2={y(0)} stroke={grid} />

        {yTicks.map((t, i) => (
          <g key={i} transform={`translate(0,${y(t)})`}>
            <line x1={W - M.right} x2={W - M.right + 6} y1={0} y2={0} stroke={axis} />
            <text x={W - M.right + 10} y={4} fontSize="12" fill={axis}>{t}</text>
          </g>
        ))}
        <text x={W - M.right + 12} y={M.top - 12} fontSize="12" fill={axis}>%</text>

        {!noData &&
          segments.map((s, i) => {
            const st = styleFor(s.completed);
            return (
              <line
                key={i}
                x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                stroke={purple}
                strokeWidth={6}
                strokeLinecap="round"
                strokeDasharray={st.dash}
                strokeOpacity={st.opacity}
              />
            );
          })}

        {xTicks.map((t, i) => (
          <text key={i} x={x(t)} y={H - M.bottom + 28} textAnchor="middle" fontSize="14" fill={text}>
            {d3.timeFormat("%b %d")(t)}
          </text>
        ))}

        {hover && (
          <>
            <line x1={hover.x} x2={hover.x} y1={M.top} y2={H - M.bottom} stroke={grid} />
            <circle cx={hover.x} cy={hover.y} r={5} fill={purple} />
            <g transform={`translate(${Math.min(W - M.right - 200, Math.max(M.left, hover.x + 12))},${Math.max(M.top + 8, hover.y - 44)})`}>
              <rect width="180" height="56" rx="8" ry="8" fill="#fff" stroke={grid} />
              <text x="12" y="22" fontSize="13" fill={text}>{d3.timeFormat("%b %d")(hover.date)} • {hover.pct}%</text>
              <text x="12" y="40" fontSize="12" fill={axis}>{t("rof.progression_chart.completed")}{hover.completed}/3</text>
            </g>
          </>
        )}

        <g transform={`translate(${legendX},${legendY})`}>
          <text x="0" y="0" fontSize="14" fill={axis}>{t("rof.progression_chart.completed")}</text>

          {/* 1/3 */}
          <g transform="translate(130, 0)">
            <text x="0" y="-8" fontSize="12" fill={axis}>1/3</text>
            <line x1="0" y1="0" x2="42" y2="0"
                  stroke={purple} strokeWidth="6" strokeLinecap="round"
                  strokeDasharray="4 14" strokeOpacity="0.5" />
          </g>

          {/* 2/3 */}
          <g transform="translate(210, 0)">
            <text x="0" y="-8" fontSize="12" fill={axis}>2/3</text>
            <line x1="0" y1="0" x2="42" y2="0"
                  stroke={purple} strokeWidth="6" strokeLinecap="round"
                  strokeDasharray="10 10" strokeOpacity="0.75" />
          </g>

          {/* 3/3 */}
          <g transform="translate(290, 0)">
            <text x="0" y="-8" fontSize="12" fill={axis}>3/3</text>
            <line x1="0" y1="0" x2="42" y2="0"
                  stroke={purple} strokeWidth="6" strokeLinecap="round"
                  strokeOpacity="1" />
          </g>
        </g>

        {noData && (
          <text
            x={(W / 2)}
            y={(H / 2)}
            textAnchor="middle"
            fontSize="14"
            fill={axis}
          >
            {t("rof.progression_chart.no_scores")}
          </text>
        )}
      </svg>
    </div>
  );
}
